import React, { useState } from 'react'
import { MealAssignment, AssignmentMap, MealType, RecipeRecord } from '@talktoast/shared'
import { getAssignmentKey } from '@talktoast/shared'
import { MEAL_TYPES } from './calendarConstants'

interface CalendarMobileViewProps {
  weekDates: Array<{
    key: string
    label: string
    fullLabel: string
    date: number
    fullDate: Date
    isToday: boolean
    dateStr: string
  }>
  assignments: AssignmentMap
  unassignedRecipes: RecipeRecord[]
  enabledMealTypes: MealType[]
  onAssign: (params: { recipeId: string; date: string; mealType: MealType }) => void
  onRemove: (params: { date: string; mealType: MealType }) => void
  onRecipeClick: (assignment: MealAssignment) => void
  selectedDate: string
  onSelectedDateChange: (date: string) => void
}

export const CalendarMobileView: React.FC<CalendarMobileViewProps> = ({
  weekDates,
  assignments,
  unassignedRecipes,
  enabledMealTypes,
  onAssign,
  onRemove,
  onRecipeClick,
  selectedDate,
  onSelectedDateChange
}) => {
  const [showRecipeModal, setShowRecipeModal] = useState(false)
  const [selectedMealTypeForModal, setSelectedMealTypeForModal] = useState<MealType | null>(null)

  const filteredMealTypes = MEAL_TYPES.filter(mt =>
    enabledMealTypes.includes(mt.key as MealType)
  )

  // Group unassigned recipes by meal type
  const unassignedRecipesByMealType = unassignedRecipes.reduce((acc, recipe) => {
    const mealType = recipe.recipe_data.mealType as MealType
    if (!acc[mealType]) {
      acc[mealType] = []
    }
    acc[mealType].push(recipe)
    return acc
  }, {} as Record<MealType, RecipeRecord[]>)

  const handleMobileRecipeSelect = (recipe: { id: string; name: string; mealType: MealType }) => {
    if (!selectedMealTypeForModal) return

    onAssign({
      recipeId: recipe.id,
      date: selectedDate,
      mealType: selectedMealTypeForModal
    })

    setShowRecipeModal(false)
    setSelectedMealTypeForModal(null)
  }

  return (
    <>
      {/* Horizontal Day Selector */}
      <div className="bg-white border-b sticky top-[72px] z-10">
        <div className="flex px-2 py-3 gap-1">
          {weekDates.map((day) => (
            <button
              key={day.key}
              onClick={() => onSelectedDateChange(day.dateStr)}
              className={`flex-1 py-3 px-1 rounded-lg transition-all ${
                selectedDate === day.dateStr
                  ? 'bg-blue-600 text-white shadow-md'
                  : day.isToday
                  ? 'bg-blue-50 text-blue-600 border-2 border-blue-200'
                  : 'bg-gray-50 text-gray-700'
              }`}
            >
              <div className="text-xs font-medium">{day.label}</div>
              <div className={`text-lg font-bold ${
                selectedDate === day.dateStr ? 'text-white' : day.isToday ? 'text-blue-600' : 'text-gray-900'
              }`}>
                {day.date}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Vertical Meal Stack */}
      <div className="px-4 py-4 space-y-4">
        {filteredMealTypes.map((mealType) => {
          const assignmentKey = getAssignmentKey(selectedDate, mealType.key as MealType)
          const assignment = assignments.get(assignmentKey)
          const availableRecipes = unassignedRecipesByMealType[mealType.key as MealType] || []

          // Hide if no assignment AND no available recipes
          if (!assignment && availableRecipes.length === 0) return null

          return (
            <div key={mealType.key} className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Meal Type Header */}
              <div className={`bg-${mealType.color}-50 px-4 py-3 border-b border-${mealType.color}-100`}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{mealType.emoji}</span>
                  <span className="font-semibold text-gray-900">{mealType.label}</span>
                </div>
              </div>

              {/* Meal Assignment */}
              <div className="p-4">
                {assignment ? (
                  <div className={`bg-${mealType.color}-100 border-2 border-${mealType.color}-200 rounded-lg p-4 relative`}>
                    <button
                      onClick={() => onRecipeClick(assignment)}
                      className={`font-semibold text-${mealType.color}-900 mb-2 hover:underline text-left w-full`}
                    >
                      {assignment.recipeName}
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedMealTypeForModal(mealType.key as MealType)
                          setShowRecipeModal(true)
                        }}
                        className={`flex-1 ${
                          mealType.key === 'dessert'
                            ? 'bg-pink-600 text-white border-2 border-pink-700'
                            : `bg-${mealType.color}-600 text-white`
                        } px-4 py-2 rounded-lg font-medium active:bg-${mealType.color}-700`}
                      >
                        Change Recipe
                      </button>
                      <button
                        onClick={() => onRemove({
                          recipeId: assignment.recipeId,
                          date: selectedDate,
                          mealType: mealType.key as MealType
                        })}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium active:bg-red-100 border border-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setSelectedMealTypeForModal(mealType.key as MealType)
                      setShowRecipeModal(true)
                    }}
                    className={`w-full border-2 border-dashed border-${mealType.color}-300 rounded-lg py-8 text-center hover:border-${mealType.color}-400 hover:bg-${mealType.color}-50 transition-colors`}
                  >
                    <div className="text-3xl mb-2">{mealType.emoji}</div>
                    <div className="font-medium text-gray-700">Add {mealType.label}</div>
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Mobile Recipe Selector Modal (Bottom Sheet) */}
      {showRecipeModal && selectedMealTypeForModal && (
        <div className="fixed inset-0 z-50 flex items-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setShowRecipeModal(false)
              setSelectedMealTypeForModal(null)
            }}
          />

          {/* Bottom Sheet */}
          <div className="relative w-full bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up">
            {/* Handle Bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                {MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal) && (
                  <>
                    <span className="text-2xl">
                      {MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal)?.emoji}
                    </span>
                    <h2 className="text-lg font-bold">
                      Select {MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal)?.label}
                    </h2>
                  </>
                )}
              </div>
              <button
                onClick={() => {
                  setShowRecipeModal(false)
                  setSelectedMealTypeForModal(null)
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Recipe List */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {(() => {
                const mealTypeInfo = MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal)
                const availableRecipes = unassignedRecipesByMealType[selectedMealTypeForModal] || []

                // Also show currently assigned recipe (if any) as an option
                const assignmentKey = getAssignmentKey(selectedDate, selectedMealTypeForModal)
                const currentAssignment = assignments.get(assignmentKey)

                const allRecipes = currentAssignment
                  ? [
                      ...unassignedRecipes.filter(r => r.id === currentAssignment.recipeId),
                      ...availableRecipes
                    ]
                  : availableRecipes

                if (allRecipes.length === 0) {
                  return (
                    <div className="text-center py-12 text-gray-500">
                      <div className="text-4xl mb-4">🍽️</div>
                      <p className="font-medium">No recipes available</p>
                      <p className="text-sm">All recipes have been assigned to the calendar.</p>
                    </div>
                  )
                }

                return (
                  <div className="space-y-3">
                    {allRecipes.map((recipe) => {
                      const isCurrentlyAssigned = currentAssignment?.recipeId === recipe.id

                      return (
                        <button
                          key={recipe.id}
                          onClick={() => handleMobileRecipeSelect({
                            id: recipe.id,
                            name: recipe.recipe_data.name,
                            mealType: recipe.recipe_data.mealType as MealType
                          })}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                            isCurrentlyAssigned
                              ? `bg-${mealTypeInfo?.color}-100 border-${mealTypeInfo?.color}-300`
                              : `bg-${mealTypeInfo?.color}-50 border-${mealTypeInfo?.color}-200 hover:border-${mealTypeInfo?.color}-400 active:scale-[0.98]`
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className={`font-semibold mb-1 ${
                                isCurrentlyAssigned ? `text-${mealTypeInfo?.color}-900` : 'text-gray-900'
                              }`}>
                                {recipe.recipe_data.name}
                                {isCurrentlyAssigned && (
                                  <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                    Current
                                  </span>
                                )}
                              </div>
                              {recipe.recipe_data.description && (
                                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                  {recipe.recipe_data.description}
                                </p>
                              )}
                              <div className="text-xs text-gray-500">
                                {recipe.recipe_data.prepTime && `${recipe.recipe_data.prepTime}min prep`}
                                {recipe.recipe_data.prepTime && recipe.recipe_data.cookTime && ' • '}
                                {recipe.recipe_data.cookTime && `${recipe.recipe_data.cookTime}min cook`}
                              </div>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
